export const EMR_CHECK_IN_ZONE_EXACT_PATHS = [
  '/mentor-home/manage-my-bookings',
]

export const EMR_CHECK_IN_ZONE_PREFIXES = [
  '/mentor-home/my-clinic-associations',
  '/mentor-home/join-chamber-room',
  '/mentor-home/audit-chamber-room',
  '/mentor-home/join-as-auditor',
  '/mentor-home/my-calendar',
  '/mentor-home/prescription',
  '/mentor-home/therapy-report',
  '/mentor-home/screening-report',
]

export const EMR_CHECK_IN_ZONE_DYNAMIC_ROUTE_DESCRIPTIONS = [
  '/l/:bookedEventId',
  '/live/:bookedEventId',
  '/webview/l/:bookedEventId',
  '/webview/live/:bookedEventId',
  '/webinar/:bookedEventId',
]

export const EMR_VIDEO_CALL_ACTIVITY_STORAGE_KEY = 'emrVideoCallActivity'
export const EMR_VIDEO_CALL_ACTIVITY_EVENT = 'emr-video-call-activity-change'
export const EMR_CHAMBER_AUTO_CONNECT_PAUSE_STORAGE_KEY = 'emrChamberAutoConnectPause'
export const EMR_CHAMBER_AUTO_CONNECT_PAUSE_EVENT = 'emr-chamber-auto-connect-pause-change'

const EMR_CHECK_IN_ZONE_DYNAMIC_PATTERNS = [
  /^\/l\/[^/]+(?:\/.*)?$/,
  /^\/live\/[^/]+(?:\/.*)?$/,
  /^\/webview\/l\/[^/]+(?:\/.*)?$/,
  /^\/webview\/live\/[^/]+(?:\/.*)?$/,
  /^\/webinar\/[^/]+(?:\/.*)?$/,
]

const EMR_VIDEO_CALL_ROUTE_PATTERNS = [
  /^\/mentor-home\/join-chamber-room\/[^/]+\/[^/]+\/l\/[^/]+(?:\/.*)?$/,
]

function normalizePathname(pathname = '') {
  const path = pathname.split('?')[0].split('#')[0] || '/'
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

export function isEmrCheckInZonePath(pathname = '') {
  const path = normalizePathname(pathname)

  if (EMR_CHECK_IN_ZONE_EXACT_PATHS.includes(path)) {
    return true
  }

  if (
    EMR_CHECK_IN_ZONE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
  ) {
    return true
  }

  return EMR_CHECK_IN_ZONE_DYNAMIC_PATTERNS.some((pattern) => pattern.test(path))
}

export function isEmrVideoCallRoutePath(pathname = '') {
  const path = normalizePathname(pathname)
  return EMR_VIDEO_CALL_ROUTE_PATTERNS.some((pattern) => pattern.test(path))
}

export function isEmrChamberAutoConnectRoutePath(pathname = '') {
  const path = normalizePathname(pathname)
  return path === '/mentor-home/join-chamber-room' || path.startsWith('/mentor-home/join-chamber-room/')
}

export function readEmrVideoCallActivity({ maxAgeMs = 45000 } = {}) {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const storedValue = window.localStorage.getItem(EMR_VIDEO_CALL_ACTIVITY_STORAGE_KEY)
    if (!storedValue) {
      return false
    }

    const parsedValue = JSON.parse(storedValue)
    const updatedAt = Number(parsedValue?.updatedAt)

    return Boolean(parsedValue?.active && updatedAt && Date.now() - updatedAt <= maxAgeMs)
  } catch {
    return false
  }
}

export function setEmrVideoCallActivity(active, details = {}) {
  if (typeof window === 'undefined') {
    return
  }

  const payload = {
    ...details,
    active: Boolean(active),
    updatedAt: Date.now(),
  }

  try {
    if (payload.active) {
      window.localStorage.setItem(EMR_VIDEO_CALL_ACTIVITY_STORAGE_KEY, JSON.stringify(payload))
    } else {
      const storedValue = window.localStorage.getItem(EMR_VIDEO_CALL_ACTIVITY_STORAGE_KEY)
      const storedPayload = storedValue ? JSON.parse(storedValue) : null

      if (payload.source && storedPayload?.source && payload.source !== storedPayload.source) {
        window.dispatchEvent(new CustomEvent(EMR_VIDEO_CALL_ACTIVITY_EVENT, { detail: payload }))
        return
      }

      window.localStorage.removeItem(EMR_VIDEO_CALL_ACTIVITY_STORAGE_KEY)
    }
  } catch {
    // Ignore storage failures; the route-level call suppression still protects /l/* surfaces.
  }

  window.dispatchEvent(new CustomEvent(EMR_VIDEO_CALL_ACTIVITY_EVENT, { detail: payload }))
}

export function readEmrChamberAutoConnectPause({ maxAgeMs = 10 * 60 * 1000 } = {}) {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const storedValue = window.localStorage.getItem(EMR_CHAMBER_AUTO_CONNECT_PAUSE_STORAGE_KEY)
    if (!storedValue) {
      return false
    }

    const parsedValue = JSON.parse(storedValue)
    const updatedAt = Number(parsedValue?.updatedAt)
    const resumeAtEpochMs = Number(parsedValue?.resumeAtEpochMs)
    const isFresh = Boolean(updatedAt && Date.now() - updatedAt <= maxAgeMs)
    const isWithinPauseWindow = Boolean(resumeAtEpochMs && resumeAtEpochMs > Date.now())

    return Boolean(parsedValue?.active && isFresh && isWithinPauseWindow)
  } catch {
    return false
  }
}

export function setEmrChamberAutoConnectPause(active, details = {}) {
  if (typeof window === 'undefined') {
    return
  }

  const payload = {
    ...details,
    active: Boolean(active),
    updatedAt: Date.now(),
  }

  try {
    if (payload.active) {
      window.localStorage.setItem(
        EMR_CHAMBER_AUTO_CONNECT_PAUSE_STORAGE_KEY,
        JSON.stringify(payload),
      )
    } else {
      window.localStorage.removeItem(EMR_CHAMBER_AUTO_CONNECT_PAUSE_STORAGE_KEY)
    }
  } catch {
    // Ignore storage failures; this signal only prevents duplicate idle/pause dialogs.
  }

  window.dispatchEvent(
    new CustomEvent(EMR_CHAMBER_AUTO_CONNECT_PAUSE_EVENT, { detail: payload }),
  )
}
